import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'
import type { SchemaDefinition, SchemaField } from '../../types'

interface Props {
  schema: SchemaDefinition
  values: Record<string, unknown>
  onChange: (name: string, value: unknown) => void
}

function renderField(field: SchemaField, value: unknown, onChange: (name: string, value: unknown) => void) {
  const label = field.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    + (field.required ? ' *' : '')

  switch (field.type) {
    case 'boolean':
      return (
        <FormControlLabel
          key={field.name}
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => onChange(field.name, e.target.checked)}
            />
          }
          label={label}
        />
      )
    case 'enum':
      return (
        <FormControl key={field.name} fullWidth size="small">
          <InputLabel>{label}</InputLabel>
          <Select
            value={value as string || ''}
            label={label}
            onChange={(e) => onChange(field.name, e.target.value)}
          >
            {(field.options || []).map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
          {field.description && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
              {field.description}
            </Typography>
          )}
        </FormControl>
      )
    case 'number':
    case 'integer':
      return (
        <TextField
          key={field.name}
          fullWidth
          size="small"
          label={label}
          type="number"
          value={value ?? field.default ?? ''}
          onChange={(e) => onChange(field.name, e.target.value)}
          helperText={field.description}
        />
      )
    default: {
      const inputType = field.format === 'password' ? 'password'
        : field.type === 'email' ? 'email'
        : 'text'
      return (
        <TextField
          key={field.name}
          fullWidth
          size="small"
          label={label}
          type={inputType}
          value={value ?? field.default ?? ''}
          onChange={(e) => onChange(field.name, e.target.value)}
          helperText={field.description}
        />
      )
    }
  }
}

export default function ConfigForm({ schema, values, onChange }: Props) {
  return (
    <Box>
      {schema.groups.map((group) => (
        <Box key={group.name} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            {group.title}
          </Typography>
          {group.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {group.description}
            </Typography>
          )}
          {group.segments.map((segment) => (
            <Box key={segment.name} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {segment.title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {segment.fields.map((field) => renderField(field, values[field.name], onChange))}
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  )
}
